import React, { useState, useEffect } from 'react';
import { networkService } from '../services/networkService';
import { syncService } from '../services/syncService';
import { useAuth } from '../contexts/AuthContext';

const OfflineStatusBar = () => {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(networkService.getNetworkStatus());
  const [syncStatus, setSyncStatus] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Listen for network changes
    const unsubscribeNetwork = networkService.addNetworkListener(setIsOnline);
    
    // Listen for sync status changes
    const unsubscribeSync = syncService.addSyncListener(setSyncStatus);

    return () => {
      unsubscribeNetwork();
      unsubscribeSync();
    };
  }, []);

  useEffect(() => {
    // Check for pending changes when user changes
    const checkPendingChanges = async () => {
      if (user?.uid) {
        try {
          const pending = await syncService.hasPendingChanges(user.uid);
          setHasPendingChanges(pending);
        } catch (error) {
          console.error('Failed to check pending changes:', error);
        }
      }
    };

    checkPendingChanges();
  }, [user?.uid, syncStatus]);

  const handleForceSync = async () => {
    if (!user?.uid || !isOnline) return;
    
    try {
      await syncService.forcSync(user.uid);
    } catch (error) {
      console.error('Force sync failed:', error);
    }
  };

  // Don't show anything if online and no pending changes
  if (isOnline && !hasPendingChanges && !syncStatus) {
    return null;
  }

  const getStatusColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (syncStatus?.type === 'start') return 'bg-blue-500';
    if (syncStatus?.type === 'success') return 'bg-green-500';
    if (syncStatus?.type === 'error') return 'bg-orange-500';
    if (hasPendingChanges) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline - Changes saved locally';
    if (syncStatus?.type === 'start') return syncStatus.message || 'Syncing...';
    if (syncStatus?.type === 'success') return syncStatus.message || 'Synced successfully';
    if (syncStatus?.type === 'error') return syncStatus.message || 'Sync failed';
    if (hasPendingChanges) return 'Changes pending sync';
    return 'Connected';
  };

  const getIcon = () => {
    if (!isOnline) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728" />
        </svg>
      );
    }
    
    if (syncStatus?.type === 'start') {
      return (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      );
    }
    
    if (syncStatus?.type === 'success') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    
    if (syncStatus?.type === 'error') {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L3.354 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    }
    
    if (hasPendingChanges) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
      </svg>
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div
        className={`${getStatusColor()} text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 cursor-pointer transition-all duration-200 hover:shadow-xl`}
        onClick={() => setShowDetails(!showDetails)}
      >
        {getIcon()}
        <span className="text-sm font-medium">{getStatusText()}</span>
        
        {/* Expand/collapse icon */}
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {/* Details panel */}
      {showDetails && (
        <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-64">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Connection Status</span>
              <span className={`text-sm ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            {hasPendingChanges && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Pending Changes</span>
                <span className="text-sm text-yellow-600">Yes</span>
              </div>
            )}
            
            {syncStatus && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Last Sync</span>
                <span className={`text-sm ${
                  syncStatus.type === 'success' ? 'text-green-600' : 
                  syncStatus.type === 'error' ? 'text-red-600' : 
                  'text-blue-600'
                }`}>
                  {syncStatus.type === 'success' ? 'Success' : 
                   syncStatus.type === 'error' ? 'Failed' : 
                   'In Progress'}
                </span>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="pt-2 border-t border-gray-100">
              {isOnline && (hasPendingChanges || syncStatus?.type === 'error') && (
                <button
                  onClick={handleForceSync}
                  className="w-full bg-primary text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-teal-700 transition-colors"
                  disabled={syncStatus?.type === 'start'}
                >
                  {syncStatus?.type === 'start' ? 'Syncing...' : 'Sync Now'}
                </button>
              )}
              
              {!isOnline && (
                <div className="text-xs text-gray-500 text-center">
                  Your changes are saved locally and will sync when you're back online.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineStatusBar;
